import {useContext, useEffect, useReducer, useState} from "react";
import moment from "moment";
import "moment/locale/de";

import {
    BannerState,
    PBanner,
    PButton,
    PButtonPure,
    PDivider, PHeading,
    PSpinner,
    PTextFieldWrapper
} from "@porsche-design-system/components-react";
import { CreateModelState, Model } from "../../store/types";
import * as actions from "../../store/actions";
import {lenaReducer} from "../../store/reducers";
import TextField from "../../components/textField/textField";
import Datepicker from "../../components/datepicker/datepicker";
import {CustomTable} from "../../components/customTable/customTable";
import {CustomTableHeader} from "../../components/customTable/customTableHeader";
import {deleteModel, getModels, saveModel, updateModel} from "../../api/ModelService";
import {getDateTimeAsString, showDateTimeAsString, splitDateTime} from "../../utils";
import UserContext, {UserContextType} from "../../store/UserContext";
import {MarketDependentSos} from "./MarketDependentSos";
import {ImporterSos, SosDateImporters} from "../../store/importerSos";
import {getImportersSosByOrderType} from "../../api/ModelImporterService";
import {useModelDispatch} from "../../store/ModelContext";
import {SaveModel, UpdateModel} from "../../store/model";
import { Grid } from "@react-css/grid";

type ModelBanner = {
    state: BannerState;
    title: string;
    description: string;
    duration: number;
};

function MaintainTab(props : any) {
    //create maintain model state
    const initialState: CreateModelState = {
        createModel: {
            orderType: "",
            description: "",
            startDate: "",
            startTime: "",
            maxLimitedNumber: "",
            startOfSaleDate: "2030-01-01",
            startOfSaleTime: "00:00",
            importerStartOfSaleDateTime: []
        },
        createModelValid: {
            orderTypeValid: true,
            descriptionValid: true,
            startDateValid: true,
            startTimeValid: true,
            maxLimitedNumberValid: true,
            startOfSaleDateValid: true,
            startOfSaleTimeValid: true
        },
        triggerSave: false,
        triggerUpdate: false,
        triggerUpdateList: false,
        updateView: false,
        isBannerShow: false,
        bannerTitle: "",
        bannerDesc: "",
        bannerState: "",
        savedStartDateInPast: false,
        savedStartOfSaleDateInPast: false
    };
    const {lenaAuthToken} = useContext<UserContextType>(UserContext);
    const [state, dispatch] = useReducer(lenaReducer, initialState);
    // context definition
    // @ts-ignore

    const [models, setModels] = useState<Model[]>([]);
    const [isBannerShow, handleBannerShow] = useState(false);
    const initialBanner: ModelBanner = {
        state: "info",
        title: "",
        description: "",
        duration: 6000
    };
    const [bannerInfo, handleBannerInfo] = useState(initialBanner);
    const [isDeleteBanner, handleDeleteBannerShow] = useState(false);
    const [delOrderType, selectDeleteModel] = useState(null);
    const [loading, handlePageLoading] = useState(false);
    const tableId = "modelTable";
    const [marketDependentSos, setMarketDependentSos] = useState<SosDateImporters[]>([]);
    const [modelImporterRequestList, setModelImporterRequestList] = useState<ImporterSos[]>([]);
    const modelContextDispatch = useModelDispatch();

    const onChangeOrderType = (value: string) => {
        dispatch(actions.changeOrderType(value));
    };

    const onChangeDescription = (value: string) => {
        dispatch(actions.changeDescription(value));
    };

    const onChangeStartDate = (value: string) => {
        let validFormat = value ? value.split(".").join("-") : null;
        dispatch(actions.changeStartDate(validFormat));
    };

    const onChangeStartTime = (value: string) => {
        dispatch(actions.changeStartTime(value));
    };

    const onChangeStartOfSaleDate = (value: string) => {
        let validFormat = value ? value.split(".").join("-") : null;
        dispatch(actions.changeStartOfSaleDate(validFormat));
    };

    const onChangeStartOfSaleTime = (value: string) => {
        dispatch(actions.changeStartOfSaleTime(value));
    };

    const onChangeMaxLimitedNumber = (value: string) => {
        dispatch(actions.changeMaxLimitedNumber(value));
    };

    const onSubmit = () => {
        let modelImporterRequestList = [];
        marketDependentSos.forEach((m) => {
            modelImporterRequestList = modelImporterRequestList.concat(m.ImporterSos);
        });
        setModelImporterRequestList(modelImporterRequestList);
        dispatch(actions.doSubmit());
    };

    const doReset = () => {
        setMarketDependentSos([]);
        dispatch(actions.doReset());
    };

    const doDelete = () => {
        dispatch(actions.doDelete());
    };

    const showBanner = (message : string, resultState : BannerState) => {
        let bannerInfo: ModelBanner = {
            state: resultState,
            title: "LENA Notification",
            description: message,
            duration: 6000
        };
        handleBannerInfo(bannerInfo);
        handleBannerShow(true);
        dispatch(actions.showBannerMsg(true, "LENA", message, resultState));
        setTimeout(() => {
            handleBannerShow(false);
        }, bannerInfo.duration);
    };

    useEffect(doDelete, []);

    useEffect(() => {
        //Save Model
        if (state.triggerSave) {
            handlePageLoading(true);
            let request: SaveModel = {
                orderType: state.createModel.orderType,
                description: state.createModel.description,
                startDateTime: getDateTimeAsString(state.createModel.startDate, state.createModel.startTime),
                startOfSaleDateTime: getDateTimeAsString(state.createModel.startOfSaleDate, state.createModel.startOfSaleTime),
                maxLimitedNumber: parseInt(state.createModel.maxLimitedNumber),
                modelImporterRequestList: modelImporterRequestList
            };
            saveModel(lenaAuthToken, request)
                .then((result) => {
                    doReset();
                    doDelete();
                    showBanner(result + " Please communicate start date and time accordingly to importers via relevant information channels.", "info");

                })
                .catch((error) => {
                    handlePageLoading(false);
                    modelContextDispatch({type: "UPDATE_MODEL_LIST", doUpdate: true});
                    showBanner(error.message, "error");
                }).finally(() => {
                handlePageLoading(false);
                modelContextDispatch({type: "UPDATE_MODEL_LIST", doUpdate: true});
            });
        }
        //Update Model
        if (state.triggerUpdate) {
            handlePageLoading(true);
            let request: UpdateModel = {
                orderType: state.createModel.orderType,
                description: state.createModel.description,
                startDateTime: getDateTimeAsString(state.createModel.startDate, state.createModel.startTime),
                startOfSaleDateTime: getDateTimeAsString(state.createModel.startOfSaleDate, state.createModel.startOfSaleTime),
                modelImporterRequestList: modelImporterRequestList
            };
            updateModel(lenaAuthToken, request)
                .then((result) => {
                    doReset();
                    doDelete();
                    showBanner(result + " Please communicate start date and time accordingly to importers via relevant information channels.", "info");
                })
                .catch((error) => {
                    showBanner(error.message, "error");
                    handlePageLoading(false);
                    modelContextDispatch({type: "UPDATE_MODEL_LIST", doUpdate: true});
                }).finally(() => {
                handlePageLoading(false);
                modelContextDispatch({type: "UPDATE_MODEL_LIST", doUpdate: true});
            });
        }
    }, [state, lenaAuthToken, modelImporterRequestList, modelContextDispatch]);

    useEffect(() => {
        if (state.triggerUpdateList || props.triggerUpdate) {
            setModels([]);
            getModels(lenaAuthToken)
                .then((models) => {
                    setModels(models);
                })
                .catch((error) => {
                    showBanner(error.message, "error");
                });
        }
    }, [state, lenaAuthToken, props.triggerUpdate, loading]);

    const [importerSosList, setImporterSosList] = useState<ImporterSos[]>([]);

    useEffect(() => {
        let orderType = state.createModel.orderType === "" ? null : state.createModel.orderType;
        if (orderType === null || orderType.length === 5) {
            getImportersSosByOrderType(lenaAuthToken, orderType)
                .then((importerSos: ImporterSos[]) => {
                    setImporterSosList(importerSos);
                    if (state.updateView) {
                        let sosList = [];
                        state.createModel.importerStartOfSaleDateTime.forEach((sos) => {
                            let importer = importerSos.filter((importer) => importer.startOfSaleDateTime === sos);
                            let importerSosItem = {
                                startOfSaleDateTime: sos,
                                ImporterSos: importer
                            };
                            sosList.push(importerSosItem);
                        });
                        setMarketDependentSos(sosList);
                    } else {
                        setMarketDependentSos([]);
                    }
                })
                .catch((error) => {
                    showBanner(error.message, "error");
                });
        }
    }, [lenaAuthToken, state.createModel.orderType, state.createModel.importerStartOfSaleDateTime, state.updateView]);

    function HandleUpdateModel(orderType: string) {
        getModels(lenaAuthToken).then((result) => {
            const selOrder = result.find((s : UpdateModel) => s.orderType === orderType);
            setModels(result);
            dispatch(
                actions.switchToUpdateModel(
                    selOrder.orderType,
                    selOrder.description,
                    selOrder.startDateTime,
                    selOrder.maxLimitedNumber,
                    selOrder.startOfSaleDateTime,
                    selOrder.importerStartOfSaleDateTime
                )
            );
        });
    }

    function HandleDelete(orderType: string) {
        handleDeleteBannerShow(false);
        selectDeleteModel(orderType);
        setTimeout(() => {
            handleDeleteBannerShow(true);
        }, 100);
    }

    function handleDeleteConfirm() {
        if (delOrderType === null) return;
        handleDeleteBannerShow(false);
        handlePageLoading(true);
        deleteModel(lenaAuthToken, delOrderType)
            .then((result) => {
                selectDeleteModel(null);
                doDelete();
                showBanner(result, "info");
            })
            .catch((error) => {
                handlePageLoading(false);
                modelContextDispatch({type: "UPDATE_MODEL_LIST", doUpdate: true});
                showBanner(error.message, "error");
            }).finally(() => {
            handlePageLoading(false);
            modelContextDispatch({type: "UPDATE_MODEL_LIST", doUpdate: true});
        });
    }

    function handleDeleteCancel() {
        selectDeleteModel(null);
        handleDeleteBannerShow(false);
    }

    function validateSavedStartDateWithNow(dateTimeJson: any) {
        let locale = navigator.language;
        moment.locale(locale);
        let dateTime = splitDateTime(dateTimeJson);
        return moment.utc(moment.now()).unix() > moment.utc(new Date(dateTime.date + " " + dateTime.time)).unix();
    }

    /**
     * adding new MarketDependentSos-Components
     */
    function addMarketDependentSos() {
        let sos = [
            ...marketDependentSos,
            {
                startOfSaleDateTime: "",
                ImporterSos: []
            }
        ];
        setMarketDependentSos(sos);
    }

    function updateMarketDependentSosList(index: number, startOfSaleDateTime: string, importerSos: ImporterSos[]) {

        marketDependentSos[index].startOfSaleDateTime = startOfSaleDateTime;
        let currentImporterSos = marketDependentSos[index].ImporterSos;
        let importerSosToDelete = [];

        //find all importerSos where startOfSaleDateTime had to be set empty

        currentImporterSos.forEach(item => {
            if (!importerSos.find(i => i.importer === item.importer)) {
                importerSosToDelete.push(item);
            }
        })
        marketDependentSos[index].ImporterSos = importerSos;

        let importerSosListTemp = [...importerSosList];
        importerSos.forEach((item) => {
            let importerSos = importerSosListTemp.find((importerSos) => importerSos.importer === item.importer);
            importerSos.startOfSaleDateTime = item.startOfSaleDateTime;
        });

        //set startOfSaleDateTime empty for all importerSos in importerSosToDelete array
        importerSosToDelete.forEach((item) => {
            let importerSos = importerSosListTemp.find((importerSos) => importerSos.importer === item.importer);
            importerSos.startOfSaleDateTime = null;
        });
        setImporterSosList(importerSosListTemp);
    }


    return (
        <div className="wrapper">
            {isBannerShow && (
                <PBanner state={bannerInfo.state} dismissButton={true} open={isBannerShow} autoFocus={false} inert={false} style={{textAlign:"left"}}
                   heading={bannerInfo.title} description={bannerInfo.description}>
                </PBanner>
            )}
            {isDeleteBanner && (
                <PBanner
                    dismissButton={true}
                    onDismiss={() => handleDeleteCancel()}
                    open={isDeleteBanner} autoFocus={false}
                    inert={false}
                heading={"Do you really want to delete model " + delOrderType + "?"}>
                    <div slot="description" className="delete-confirm-banner__description">
                        <PButton variant={"secondary"} className="confirm-button" onClick={() => handleDeleteCancel()} autoFocus={false}
                                 inert={false}>
                            Cancel
                        </PButton>
                        <PButton variant="primary" className="confirm-button" onClick={() => handleDeleteConfirm()}
                                 autoFocus={false} inert={false}>
                            Ok
                        </PButton>
                    </div>
                </PBanner>
            )}

            <div className={"create-model__input_container"}>
                <div className={"create-model__input_container__subsection"}>
                    <PHeading tag="h5" size="small" autoFocus={false} style={{marginBottom: "20px"}}>
                        {state.updateView ? "Edit limited edition" : "Create new limited edition"}
                    </PHeading>
                    <div className={"row"}>
                        <Grid autoFlowRow={true} rowGap={'1.5em'} gap='1em' columns='[first] 50% [line2] 50%'>
                            <Grid.Item>
                                <TextField
                                  placeholder={"Please type in 5 digit model code*"}
                                  label={"Model"}
                                  onChange={(value) => onChangeOrderType(value)}
                                  isError={!state.createModelValid.orderTypeValid}
                                  errorMsg="Model has to be 5 digit code."
                                  value={state.createModel.orderType}
                                  disabled={state.updateView}
                                  className={"create-model__input_section__field"}
                                />
                            </Grid.Item>
                            <Grid.Item>
                                <TextField
                                    placeholder={"Please type in model description*"}
                                    label={"Model description"}
                                    onChange={(value) => onChangeDescription(value)}
                                    isError={!state.createModelValid.descriptionValid}
                                    errorMsg="Model description is a mandatory field."
                                    value={state.createModel.description}
                                    className={"create-model__input_section__field"}
                                />
                            </Grid.Item>
                            <Grid.Item>
                                <div className={"create-model-details__input_container__subsection flex-row"}>
                                    <div className="flex-3">
                                        <Datepicker
                                            className={"create-model-details__input_section__field"}
                                            placeholder={"Please type in a start date*"}
                                            value={state.createModel.startDate}
                                            onChange={(value) => onChangeStartDate(value)}
                                            isError={!state.createModelValid.startDateValid}
                                            errorMsg="Date is a mandatory field."
                                            label={"Start date"}
                                            disabled={state.updateView && state.savedStartDateInPast}
                                        />
                                    </div>
                                    <PTextFieldWrapper className={"create-model-details__input_section__field"}
                                           autoFocus={false} inert={false}>
                                            <input name="startTime"
                                                onChange={(event) => onChangeStartTime(event.target.value)}
                                                value={state.createModel.startTime}
                                                type={"time"}
                                                disabled={state.updateView && state.savedStartDateInPast}
                                            />
                                        <span slot="message" id={"error-message-"+props.id} className="validation-message">{props.errorMsg}</span>
                                    </PTextFieldWrapper>
                                </div>
                            </Grid.Item>
                            <Grid.Item>
                                <TextField
                                    className={"create-model-details__input_section__field"}
                                    placeholder={"Please type in limitation*"}
                                    label={"Limitation"}
                                    onChange={(value) => onChangeMaxLimitedNumber(value)}
                                    isError={!state.createModelValid.maxLimitedNumberValid}
                                    errorMsg="Limitation has to be a positiv natural number."
                                    value={state.createModel.maxLimitedNumber}
                                    disabled={state.updateView}
                                />
                            </Grid.Item>
                            <Grid.Item>
                                <div className={"create-model-details__input_container__subsection flex-row"}>
                                    <div className="flex-3">
                                        <Datepicker
                                            className={"create-model-details__input_section__field"}
                                            placeholder={"Please type in SOS date"}
                                            value={state.createModel.startOfSaleDate}
                                            onChange={(value) => onChangeStartOfSaleDate(value)}
                                            isError={!state.createModelValid.startOfSaleDateValid}
                                            errorMsg="Date is a mandatory field."
                                            label={"SOS date"}
                                            disabled={state.updateView && state.savedStartOfSaleDateInPast}
                                        />
                                    </div>
                                    <PTextFieldWrapper className={"custom-field create-model-details__input_section__field"}
                                           autoFocus={false} inert={false}>
                                            <input
                                                className={
                                                    state.createModelValid.startOfSaleTimeValid === true
                                                        ? "timepicker-box text-field-input"
                                                        : "timepicker-box text-field-input error-input"
                                                }
                                                name="startOfSaleTime"
                                                onChange={(event) => onChangeStartOfSaleTime(event.target.value)}
                                                value={state.createModel.startOfSaleTime}
                                                type={"time"}
                                                disabled={state.updateView && state.savedStartOfSaleDateInPast}
                                            />
                                    </PTextFieldWrapper>
                                </div>
                            </Grid.Item>
                            <Grid.Item>
                            </Grid.Item>
                            {importerSosList.length > 0 &&
                                marketDependentSos.map((item, index) => {
                                    return (
                                        <Grid.Item id={"marketDependentSosGridItem-" + index} key={"gridItem-" + index}
                                                    className={"market-dependent-sos-grid-item"} columnStart={1} columnEnd={3}>
                                            <MarketDependentSos
                                                orderType={state.createModel.orderType}
                                                marketDependentSosItem={item}
                                                itemIndex={index}
                                                updateView={state.updateView}
                                                totalMarketDependentSos={marketDependentSos}
                                                addToMarketDependentSosList={updateMarketDependentSosList}
                                                importersSosList={importerSosList}></MarketDependentSos>
                                        </Grid.Item>
                                    );
                                })}
                            {importerSosList.length > 0 && (
                                <Grid.Item className={"add-sos-grid-item"} columnStart={1}>
                                    <PButtonPure icon={"add"} size={"small"} className={"create-model-details_addMore"}
                                                 onClick={addMarketDependentSos} autoFocus={false} inert={false}>
                                        Add more SOS
                                    </PButtonPure>
                                </Grid.Item>
                            )}
                            <Grid.Item columnStart={1} columnEnd={3} className="submit-button">
                                <PButton className="button cancel-button" variant="secondary" onClick={doReset} disabled={loading}
                                         autoFocus={false} inert={false}>
                                    Cancel
                                </PButton>
                                <PButton className="button" autoFocus={false} variant={"primary"} onClick={onSubmit}
                                         loading={loading} disabled={loading} inert={false}>
                                    Save
                                </PButton>
                            </Grid.Item>
                        </Grid>
                    </div>
                </div>
            </div>
            <PDivider className="divider" autoFocus={false} inert={false}></PDivider>
            <PHeading className={"headline-model-overview"} tag="h5" size="small" autoFocus={false} inert={false}>
                Model overview
            </PHeading>

            {loading && <PSpinner size={{base: "small", l: "medium"}} autoFocus={false} inert={false}/>}
            {!loading && (
                <CustomTable tableId={tableId}>
                    <thead>
                    <tr>
                        <CustomTableHeader text={"Model"} tableId={tableId} columnPos={0} sortable={false}/>
                        <CustomTableHeader text={"Model description"} tableId={tableId} columnPos={1} sortable={false}/>
                        <CustomTableHeader text={"Start date"} tableId={tableId} columnPos={2} sortable={false}
                                           isDate={true}/>
                        <CustomTableHeader text={"SOS date"} tableId={tableId} columnPos={3} sortable={false}
                                           isDate={true}/>
                        <CustomTableHeader text={"Limitation"} tableId={tableId} columnPos={4} sortable={false}/>
                        <CustomTableHeader text={""} tableId={tableId} columnPos={5} sortable={false}/>
                        <CustomTableHeader text={""} tableId={tableId} columnPos={6} sortable={false}/>
                    </tr>
                    </thead>
                    <tbody>
                    {Array.from(models).map((model : any) => (
                        <tr key={model.orderType}>
                            <td>{model.orderType}</td>
                            <td>{model.description}</td>
                            <td>{showDateTimeAsString(model.startDateTime)}</td>
                            <td>{showDateTimeAsString(model.startOfSaleDateTime)}</td>
                            <td>{model.maxLimitedNumber}</td>
                            <td>
                                <PButton variant="secondary" disabled={validateSavedStartDateWithNow(model.startDateTime)}
                                         onClick={() => HandleDelete(model.orderType)} autoFocus={false} inert={false}>
                                    Delete
                                </PButton>

                            </td>
                            <td>
                                <PButton variant="primary" onClick={() => HandleUpdateModel(model.orderType)}
                                         autoFocus={false} inert={false}>
                                    Change
                                </PButton>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </CustomTable>
            )}
        </div>
    );
}
export default MaintainTab;